import axios from 'axios';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '@tanstack/react-query';

import useStore from 'Hooks/useStore';
import { appConfigStore } from 'Models/AppConfigStore';
import { PricingStrategyProfileEntity, RouteEntity, UserEntity } from 'Models/Entities';
import locationAndRoutesStore from 'Models/LocationAndRoutesStore';
import passengerTypeStore from 'Models/PassengerTypeStore';
import { AccountFormValues } from 'Modules/UserAccount/Hooks/useAccountEditSession';
import { userAccountKeys } from 'Modules/UserAccount/Hooks/userAccountKeys';
import { BulkPricingAction, UserBulkPricingTool } from 'Modules/UserAccount/Pricing/UserBulkPricingTool';
import { UserAddOnsPricingTable } from 'Modules/UserAccount/Pricing/UserAddOnsPricingTable';
import { UserFreightPricingTable } from 'Modules/UserAccount/Pricing/UserFreightPricingTable';
import { UserMeasurementsPricingTable } from 'Modules/UserAccount/Pricing/UserMeasurementsPricingTable';
import { UserPassengerPricingTable } from 'Modules/UserAccount/Pricing/UserPassengerPricingTable';
import {
	PricingSections,
	UserAddOnPriceRow,
	UserPriceRow,
	applyCellToRow,
	clearAddOnRows,
	clearSectionRows,
	derivePricingSections,
	ensureAllRoutesRows,
	pricesInSyncWithDefault,
	defaultPricingToRows,
	seedAddOnRows,
	seedPriceRows,
	upsertPriceRow,
} from 'Modules/UserAccount/Pricing/userPricingRows';
import { vehicleTrailerTitle } from 'Modules/FerrySchedule/Components/FerryTripCRUD/Tabs/PricingTab/vehicleTrailerTitle';
import { getAllUserPricingEntities } from 'Services/Api/_HumanWritten/UserFixedPricingService';
import { useDefaultPricingDetail } from 'Modules/DefaultPricing/Hooks/useDefaultPricingDetail';
import { getMessageByKey } from 'Stores/LanguageStore';
import { isNotNullOrUndefined, isNullOrUndefined } from 'Util/TypeGuards';
import { ROUTES } from 'Constants';
import { Button } from 'Views/Components/Button/Button';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import If from 'Views/Components/If/If';
import { TextField } from 'Views/Components/TextBox/TextBox';
import { FormErrors } from 'Util/_HumanWritten/Errors/FormErrors';
import { AccountDetailsTabHeader } from '../AccountDetailsTabHeader';
import { CollapseExpand } from './CollapseExpand';
import { ReadOnlyField } from './ReadOnlyField';
import { PricingStrategyProfileIds } from 'Global/Pricing/PricingStrategyProfile';
import { PricingStrategyIds } from 'Global/Pricing/PricingStrategy';
import { PriceCellState } from 'Global/Pricing/PriceCellState';
import { useUserDisplayNamesStore } from 'Global/User/UserDisplayNameStore';

export interface UserPricingFieldsProps {
	/** The account edit session's form — the pricing rows live in it alongside the scalars. */
	form: UseFormReturn<AccountFormValues>;
	mode: EntityFormMode;
	setMode: (mode: EntityFormMode) => void;
	setNeedConfirm: (value: boolean) => void;
	errors: FormErrors<UserEntity>;
	canEdit?: boolean;
}

/**
 * The Pricing tab, fully form-state-driven: rows are plain objects in the edit session's form
 * (prices / addOnsPrices), edited immutably through the Modules/UserAccount/Pricing table forks;
 * entities only appear again at the save boundary. The user-type combobox that used to live
 * here is gone for good — type assignment happens in the Account tab's UserTypeSection.
 *
 * Still an observer: locationAndRoutesStore populates asynchronously during app bootstrap, and
 * the routes memo below must recompute when it lands (a hard refresh deep-linked to this tab
 * can render first).
 */
function UserPricingFields({ form, mode, setMode, setNeedConfirm, errors, canEdit = true }: UserPricingFieldsProps) {
	const { isManager } = useStore();
	const { userDisplayNames } = useUserDisplayNamesStore();

	const userDisplayNameId = useWatch({ control: form.control, name: 'userDisplayNameId' });
	const prices = (useWatch({ control: form.control, name: 'prices' }) ?? []) as UserPriceRow[];
	const addOnsPrices = (useWatch({ control: form.control, name: 'addOnsPrices' }) ?? []) as UserAddOnPriceRow[];
	const additionalInformation = useWatch({ control: form.control, name: 'additionalInformation' });

	const userDisplayName = userDisplayNames.find(x => x.id === userDisplayNameId);
	const { data: linkedDefaultPricing } = useDefaultPricingDetail(userDisplayName?.defaultPricingId);

	const [passengerPricingOpen, setPassengerPricingOpen] = useState<boolean>(false);
	const [vehicleMeasurementPricingOpen, setVehicleMeasurementPricingOpen] = useState<boolean>(false);
	const [trailerMeasurementPricingOpen, setTrailerMeasurementPricingOpen] = useState<boolean>(false);
	const [addOnsPricingOpen, setAddOnsPricingOpen] = useState<boolean>(false);
	const [freightCategoryPricingOpen, setFreightCategoryPricingOpen] = useState<boolean>(false);
	const [freightHandlingPricingOpen, setFreightHandlingPricingOpen] = useState<boolean>(false);
	const [syncTrigger, setSyncTrigger] = useState(0);

	const [selectedSections, setSelectedSections] = useState<PricingSections>({
		passenger: false,
		vehicleTrailer: false,
		trailer: false,
		addOns: false,
		freightCategory: false,
		freightHandling: false,
	});

	// Re-derive the section checkboxes whenever the rows change — the legacy MobX reaction
	// fired on any tracked price mutation and overwrote the whole selection; this mirrors it
	// (a manual check without amounts survives until the next row edit, exactly as before).
	useEffect(() => {
		setSelectedSections(derivePricingSections(prices, addOnsPrices));
	}, [prices, addOnsPrices]);

	useEffect(() => {
		if (Object.keys(errors).includes('prices')) {
			setVehicleMeasurementPricingOpen(true);
			setTrailerMeasurementPricingOpen(true);
			setFreightCategoryPricingOpen(true);
		}
		if (Object.keys(errors).includes('adultPrice')) {
			setPassengerPricingOpen(true);
		}
		if (Object.keys(errors).includes('addOnsPrices')) {
			setAddOnsPricingOpen(true);
		}
	}, [errors]);

	// The default pricing-strategy profile is global and effectively static — cache it for the
	// session rather than refetching per mount.
	const { data: pricingStrategyProfile, isError: pricingStrategyErrored } = useQuery({
		queryKey: userAccountKeys.pricingStrategyProfile(),
		queryFn: async () => {
			const res = await axios.get<PricingStrategyProfileEntity>(
				`/api/entity/PricingStrategyProfileEntity/options/${PricingStrategyProfileIds.Default}`,
			);
			if (!res.data) throw new Error('No pricing strategy found');
			return new PricingStrategyProfileEntity(res.data);
		},
		staleTime: Infinity,
	});
	const pricingStrategy = useMemo(() => {
		return pricingStrategyProfile?.passengerStrategyId === PricingStrategyIds.ConsecutiveStops
			? 'ConsecutiveStops'
			: 'Fixed';
	}, [pricingStrategyProfile?.passengerStrategyId]);

	// Depend on the resolved strategy id (a primitive), not the whole query
	// response — a new object identity per render would recompute `routes`
	// every render and re-run the seed effect each time.
	const routes = useMemo(() => {
		const _routes = locationAndRoutesStore.routes.slice().sort((a, b) => a.name.localeCompare(b.name));
		if (pricingStrategyProfile?.passengerStrategyId === PricingStrategyIds.ConsecutiveStops) return _routes;
		return [new RouteEntity({ name: 'Default' }), ..._routes];
	}, [locationAndRoutesStore.routes, pricingStrategyProfile?.passengerStrategyId]);

	// Global form metadata (measurement types, freight ranges, add-on options). Not
	// user-specific; the query result's stable identity keeps the seed effect quiet.
	const { data: fetchedFormData } = useQuery({
		queryKey: userAccountKeys.pricingFormData(),
		queryFn: getAllUserPricingEntities,
		staleTime: Infinity,
	});
	const formData = fetchedFormData ?? null;

	// Seed placeholder rows for every (route × measurement / freight range / add-on)
	// combination the user is missing, so the pricing tables show a row per route even when
	// the user has no saved price for it. Re-runs whenever routes finish loading, the strategy
	// resolves, or sync clears the prices. Seeding is not a user edit: it neither dirties the
	// form nor arms the navigation-away guard (legacy parity).
	useEffect(() => {
		if (isNullOrUndefined(userDisplayNameId)) return;
		if (formData == null) return;

		const seedContext = {
			formData,
			routes,
			pricingStrategy,
			vehicleEnabled: appConfigStore.vehicleEnabled,
			trailerHasOwnPricing: appConfigStore.trailerHasOwnPricing,
			freightEnabled: appConfigStore.freightEnabled,
		} as const;
		form.setValue('prices', seedPriceRows(form.getValues('prices') ?? [], seedContext));
		form.setValue(
			'addOnsPrices',
			seedAddOnRows(form.getValues('addOnsPrices') ?? [], formData.additionalBookingOptions ?? []),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userDisplayNameId, formData, routes, pricingStrategy, syncTrigger]);

	const setPrices = (next: UserPriceRow[]) => {
		form.setValue('prices', next);
		setNeedConfirm(true);
	};
	const setAddOns = (next: UserAddOnPriceRow[]) => {
		form.setValue('addOnsPrices', next);
		setNeedConfirm(true);
	};

	const writePriceCell = (target: UserPriceRow, next: PriceCellState) => {
		setPrices(upsertPriceRow(form.getValues('prices') ?? [], target, next));
	};

	const writeAddOnPrice = (target: UserAddOnPriceRow, price: number | null) => {
		setAddOns(
			(form.getValues('addOnsPrices') ?? []).map(row =>
				row.additionalBookingOptionId === target.additionalBookingOptionId ? { ...row, price } : row,
			),
		);
	};

	// Recomputed every render (cell edits re-render via useWatch), matching the legacy
	// per-mutation recompute. Cheap — two map walks over the rows.
	const isInSync = pricesInSyncWithDefault(prices, linkedDefaultPricing);

	const handleSyncWithDefaultPricing = () => {
		if (!linkedDefaultPricing) return;
		// The default prices only the rows it defines; bump syncTrigger so the seed effect
		// re-runs against them and backfills placeholder rows for every measurement/route/
		// freight range the default leaves as Standard.
		const rows = defaultPricingToRows(linkedDefaultPricing);
		form.setValue('prices', rows.prices);
		form.setValue('addOnsPrices', rows.addOnsPrices);
		setSyncTrigger(t => t + 1);
		setNeedConfirm(true);
	};

	if (pricingStrategyErrored) {
		return <></>;
	}

	const handleSectionChange = (section: keyof typeof selectedSections, checked: boolean) => {
		setSelectedSections(prev => ({
			...prev,
			[section]: checked,
		}));

		if (!checked) {
			switch (section) {
				case 'passenger':
				case 'vehicleTrailer':
				case 'trailer':
				case 'freightCategory':
					// Drop the amount off each of the section's rows while keeping the
					// placeholder slot (route/strategy/id) intact.
					setPrices(clearSectionRows(form.getValues('prices') ?? [], section));
					return;
				case 'addOns':
					setAddOns(clearAddOnRows(form.getValues('addOnsPrices') ?? []));
					return;
				default:
					break;
			}
		}

		setNeedConfirm(true);
	};

	// Materialise every "All routes" placeholder row, then apply the bulk action to that layer
	// only. The seed effect re-run (syncTrigger) backfills the new rows' per-route placeholders.
	const applyBulkPricing = (action: BulkPricingAction) => {
		const ensured = ensureAllRoutesRows(form.getValues('prices') ?? [], {
			formData,
			pricingStrategy,
			vehicleEnabled: appConfigStore.vehicleEnabled,
			trailerHasOwnPricing: appConfigStore.trailerHasOwnPricing,
			freightEnabled: appConfigStore.freightEnabled,
			passengerTypes: passengerTypeStore.passengerTypes,
		});
		let cellState: PriceCellState = { kind: 'INHERIT' };
		if (action.kind === 'PERCENTAGE') {
			cellState = { kind: 'PERCENTAGE', amount: action.amount };
		} else if (action.kind === 'FIXED') {
			cellState = { kind: 'FIXED', amount: undefined };
		}
		form.setValue(
			'prices',
			ensured.map(row => (!row.routeId && !row.startStopId ? applyCellToRow(row, cellState) : row)),
		);
		setSyncTrigger(t => t + 1);
		setNeedConfirm(true);
	};

	const tableErrors = errors as { [key: string]: string | undefined };
	const isReadOnlyTables = !isManager || mode === EntityFormMode.VIEW;

	return (
		<div className="account-details-user-type-container">
			<AccountDetailsTabHeader
				headerText="Pricing"
				buttonText={mode === EntityFormMode.VIEW && isManager && canEdit ? 'Edit' : ''}
				onClick={() => setMode(EntityFormMode.EDIT)}
			/>
			{mode === EntityFormMode.VIEW && (
				<ReadOnlyField
					fieldName="Pricing"
					fieldValue={
						isNotNullOrUndefined(userDisplayNameId)
							? `Personalised - ${userDisplayName?.displayName}`
							: 'Standard'
					}
				/>
			)}
			<If condition={mode !== EntityFormMode.VIEW}>
				<p className="user-type__description">
					Pricing follows the customer type
					{isNotNullOrUndefined(userDisplayName?.displayName) ? ` (${userDisplayName?.displayName})` : ''} —
					change it on the Account tab.
				</p>
			</If>
			<If condition={isNotNullOrUndefined(userDisplayNameId)}>
				{mode === EntityFormMode.VIEW ? (
					<>
						<If condition={isNotNullOrUndefined(additionalInformation)}>
							<ReadOnlyField
								fieldName={
									isNotNullOrUndefined(userDisplayName?.additionalInformationLabelKey)
										? getMessageByKey(
												userDisplayName?.additionalInformationLabelKey ?? '',
												'Additional information',
											)
										: 'Additional information'
								}
								fieldValue={additionalInformation!}
							/>
						</If>
					</>
				) : (
					<>
						<p className="user-type__description">{userDisplayName?.description}</p>
						<If condition={userDisplayName?.additionalInformationEnabled}>
							<div className="additional-information-field__input">
								<p className="address-field__label">
									{getMessageByKey(
										userDisplayName?.additionalInformationLabelKey ?? '',
										'Additional information',
									)}
									<If condition={userDisplayName?.additionalInformationRequired ?? false}>
										<span className="required">*</span>
									</If>
								</p>
								<div
									className={classNames('custom-input-group', {
										required: errors.additionalInformation,
									})}
								>
									<Button
										className="location-map"
										icon={{ icon: 'location-map', iconPos: 'icon-right' }}
									/>
									<TextField
										className="additional-information-field"
										model={{ additionalInformation: additionalInformation ?? '' }}
										modelProperty="additionalInformation"
										label=""
										placeholder={getMessageByKey(
											userDisplayName?.additionalInformationPlaceholderKey ?? '',
											'Enter additional information',
										)}
										labelVisible={false}
										isReadOnly={!isManager}
										isRequired={userDisplayName?.additionalInformationRequired ?? false}
										onAfterChange={e => {
											form.setValue('additionalInformation', e.target.value, {
												shouldDirty: true,
											});
										}}
									/>
								</div>
								{errors.additionalInformation && (
									<p className="input-group--error">{errors.additionalInformation}</p>
								)}
							</div>
						</If>
					</>
				)}
				<If condition={isNotNullOrUndefined(linkedDefaultPricing)}>
					<div
						className={classNames(
							'flex items-center justify-between gap-3 rounded-md border p-3 text-sm',
							isInSync
								? 'border-green-300 bg-green-50 text-green-800'
								: 'border-amber-300 bg-amber-50 text-amber-800',
						)}
					>
						<span>
							{isInSync
								? `Pricing is in sync with the default pricing "${linkedDefaultPricing?.name}".`
								: // eslint-disable-next-line max-len
									`Pricing differs from the default pricing "${linkedDefaultPricing?.name}".`}
						</span>
						<If condition={!isInSync && isManager && mode !== EntityFormMode.VIEW}>
							<Button className="btn--primary btn--small !p-0" onClick={handleSyncWithDefaultPricing}>
								Sync with default
							</Button>
						</If>
					</div>
				</If>
				<If condition={isNullOrUndefined(linkedDefaultPricing) && isNotNullOrUndefined(userDisplayName)}>
					<div
						className={classNames(
							'mb-4 flex items-center justify-between gap-3 rounded-md border p-3 text-sm',
							'border-blue-300 bg-blue-50 text-blue-800',
						)}
					>
						<span>
							{/* eslint-disable-next-line max-len */}
							{`No default pricing is linked to "${userDisplayName?.displayName}". This user's pricing is managed manually.`}
						</span>
						<If condition={isManager}>
							<Button
								className="btn--primary btn--small !p-0"
								buttonProps={{
									onClick: () => {
										const url =
											`${ROUTES.ADMIN.DEFAULT_PRICING.CREATE.url()}` +
											`?scope=user&userDisplayNameId=${userDisplayName?.id}`;
										window.open(url, '_blank');
									},
								}}
							>
								Add default pricing
							</Button>
						</If>
					</div>
				</If>
				<If condition={isManager && mode !== EntityFormMode.VIEW && pricingStrategy === 'Fixed'}>
					<div className="p-3 mb-4 border border-gray-100 rounded-md bg-gray-50">
						<UserBulkPricingTool onApply={applyBulkPricing} />
					</div>
				</If>
				<div>
					<CollapseExpand
						contentName="Passenger pricing"
						content={
							<UserPassengerPricingTable
								rows={prices}
								onCellWrite={writePriceCell}
								isReadOnly={isReadOnlyTables}
								errors={tableErrors}
								pricingStrategy={pricingStrategy}
							/>
						}
						open={passengerPricingOpen}
						setOpen={(value: boolean) => setPassengerPricingOpen(value)}
						checked={selectedSections.passenger}
						onCheckedChange={checked => handleSectionChange('passenger', checked)}
						disabled={isReadOnlyTables}
					/>

					<If condition={appConfigStore.vehicleEnabled}>
						<CollapseExpand
							contentName={`${vehicleTrailerTitle()} pricing`}
							content={
								<UserMeasurementsPricingTable
									rows={prices}
									onCellWrite={writePriceCell}
									isReadOnly={isReadOnlyTables}
									errors={tableErrors}
									pricingStrategy={pricingStrategy}
									trailerPricing={false}
								/>
							}
							open={vehicleMeasurementPricingOpen}
							setOpen={(value: boolean) => setVehicleMeasurementPricingOpen(value)}
							checked={selectedSections.vehicleTrailer}
							onCheckedChange={checked => handleSectionChange('vehicleTrailer', checked)}
							disabled={isReadOnlyTables}
						/>
						<If condition={appConfigStore.trailerHasOwnPricing}>
							<CollapseExpand
								contentName="Trailer pricing"
								content={
									<UserMeasurementsPricingTable
										rows={prices}
										onCellWrite={writePriceCell}
										isReadOnly={isReadOnlyTables}
										errors={tableErrors}
										pricingStrategy={pricingStrategy}
										trailerPricing
									/>
								}
								open={trailerMeasurementPricingOpen}
								setOpen={(value: boolean) => setTrailerMeasurementPricingOpen(value)}
								checked={selectedSections.trailer}
								onCheckedChange={checked => handleSectionChange('trailer', checked)}
								disabled={isReadOnlyTables}
							/>
						</If>
					</If>
					<If condition={addOnsPrices.length > 0}>
						<CollapseExpand
							contentName="Booking add on pricing"
							content={
								<UserAddOnsPricingTable
									rows={addOnsPrices}
									onPriceChange={writeAddOnPrice}
									isReadOnly={isReadOnlyTables}
									errors={tableErrors}
								/>
							}
							open={addOnsPricingOpen}
							setOpen={(value: boolean) => setAddOnsPricingOpen(value)}
							checked={selectedSections.addOns}
							onCheckedChange={checked => handleSectionChange('addOns', checked)}
							disabled={isReadOnlyTables}
						/>
					</If>
					<If condition={appConfigStore.freightEnabled}>
						<CollapseExpand
							contentName="Freight category pricing"
							content={
								<UserFreightPricingTable
									rows={prices}
									onCellWrite={writePriceCell}
									isReadOnly={isReadOnlyTables}
									errors={tableErrors}
									pricingStrategy={pricingStrategy}
									dimension="category"
								/>
							}
							description={
								<p>
									<FormattedMessage
										id="freight-pricing__description"
										defaultMessage="Rate is either per cubic metre or per tonne, depending on which one is results in the higher price."
									/>
								</p>
							}
							open={freightCategoryPricingOpen}
							setOpen={(value: boolean) => setFreightCategoryPricingOpen(value)}
							checked={selectedSections.freightCategory}
							onCheckedChange={checked => handleSectionChange('freightCategory', checked)}
							disabled={isReadOnlyTables}
						/>
						<If
							condition={prices.some(
								x =>
									isNotNullOrUndefined(x.freightPriceRange) &&
									x.freightPriceRange?.freightHandlingId !== null,
							)}
						>
							<CollapseExpand
								contentName="Freight handling"
								content={
									<UserFreightPricingTable
										rows={prices}
										onCellWrite={writePriceCell}
										isReadOnly={isReadOnlyTables}
										errors={tableErrors}
										pricingStrategy={pricingStrategy}
										dimension="handling"
									/>
								}
								open={freightHandlingPricingOpen}
								setOpen={(value: boolean) => setFreightHandlingPricingOpen(value)}
								checked
							/>
						</If>
					</If>
				</div>
			</If>
		</div>
	);
}

export default observer(UserPricingFields);
